/**
 * An organisation's REGISTRY surfaces on the canonical record (#2267 ports 1+2).
 *
 * Two capabilities the legacy CRM record page owned and the canonical route had
 * nothing for: verifying the VAT number against VIES, and adopting a company's
 * legal identity from a business register. They arrive together because they are
 * the same subject — what an external register says this organisation is — and
 * because the lookup writes the very number the verification then checks.
 *
 * Mounted through `RECORD_READING`, the console's existing per-entity dispatch
 * for "what does this record actually look like". No `if (entity === …)` in
 * `RecordBody`, and no new registrar invented from a record panel.
 *
 * ── every write goes through the canonical writer, and is AWAITED ───────────
 * `useSaveEntity`, the same door the form saves through, so RLS and the entity's
 * declared write door apply exactly as they do to a typed field. The legacy page
 * `void`ed both of these writes; awaiting them is what turned a silent defect
 * into a visible one — see `organizationVatState`'s header for the measurement.
 *
 * ── the offer is WITHHELD, not disabled ─────────────────────────────────────
 * Both actions persist. A caller whose write capability is not positively `may`
 * is offered neither, on `FieldAction`'s doctrine: "an offer that can never be
 * taken is furniture". `unknown` fails closed, the same direction `RecordBody`
 * chose for the form itself — never flash an affordance and take it away.
 */
import { useState } from 'react'
import { Button, PageSection, ServerCheck, type ServerCheckStatus, type Tone } from '@solum/ui'
import type { Row } from '@solum/sdk'
import { useEntityRow, useSaveEntity, useWriteCapability } from '../data/queries'
import { CompanyLookupDialog } from './CompanyLookupDialog'
import { checkVat, type CompanyHit, type VatResult } from './companyLookup'
import { companyAdoptionPatch } from './companyAdoption'
import {
  isRegistryEvidence, vatVerificationView, type VatVerificationPhase,
} from './organizationVatState'
import { useAttachRegisteredAddress } from './partyAddressDoors'
import s from './OrganizationRegistry.module.css'

const ORG = 'core_organization'

/** Which of the kit's five check states a VAT reading is. The PILL still says
 *  the registry's own words and takes the view's own tone (a format-only pass
 *  is `brand`, not `good`), so this decides only what the row IS — in flight,
 *  passed, refused, unanswered or never run — never what it says. */
function checkStatus(tone: Tone | undefined, phase: VatVerificationPhase): ServerCheckStatus {
  if (phase === 'checking') return 'checking'
  if (tone == null) return 'idle'
  if (tone === 'critical') return 'failed'
  if (tone === 'warn') return 'unavailable'
  return 'ok'
}

export function OrganizationRegistry({ id }: { id: string }) {
  const rowq = useEntityRow(ORG, id)
  const save = useSaveEntity(ORG)
  const capability = useWriteCapability(ORG, id).capability
  const attachRegisteredAddress = useAttachRegisteredAddress(ORG, id)

  const [phase, setPhase] = useState<VatVerificationPhase>('stored')
  const [checked, setChecked] = useState<VatResult | undefined>(undefined)
  const [refusal, setRefusal] = useState<string | undefined>(undefined)
  const [lookupOpen, setLookupOpen] = useState(false)
  const [lookupRefusal, setLookupRefusal] = useState<string | undefined>(undefined)

  const row = (rowq.data ?? {}) as Row
  /* `vat_check` is read from the row, not from a schema the host asserts. On a
     deployment that has added a store for it this is the stored verification; on
     one that has not it is simply absent, and the badge says "not checked". */
  const stored = row.vat_check as VatResult | undefined
  const result = checked ?? stored
  const mayWrite = capability === 'may'

  const verify = async () => {
    setPhase('checking'); setRefusal(undefined)
    const outcome = await checkVat(String(row.vat_country ?? 'BE'), String(row.vat_number ?? ''))
    setChecked(outcome)
    try {
      /* AWAITED. The legacy page voided this call, which is why a refusal — and
         on every deployment measured, a refusal is exactly what comes back —
         has never once reached a person. */
      await save.mutateAsync({ id, patch: { vat_check: outcome } as Row })
      setPhase('unsaved')
    } catch (error) {
      setPhase('refused')
      setRefusal(error instanceof Error ? error.message : undefined)
    }
  }

  const adopt = async (hit: CompanyHit, attachAddress: boolean) => {
    setLookupRefusal(undefined)
    try {
      /* The registry decides the legal identity; the person's own edits to
         everything else stand. `companyAdoptionPatch` owns which is which. */
      await save.mutateAsync({ id, patch: companyAdoptionPatch(hit, row) as Row })
      if (attachAddress) await attachRegisteredAddress(hit)
    } catch (error) {
      /* Re-thrown so `Lookup` holds the dialog open on a refusal (#2149) rather
         than closing over a change that did not land. */
      setLookupRefusal(error instanceof Error ? error.message : 'The registry details could not be applied.')
      throw error
    }
  }

  const view = vatVerificationView(result, phase, refusal)

  return (
    /* ── A SECTION'S TITLE IS A HEADING (#2283 §3/§4, applied here by #2349) ─
       The mock's item 18: a record carried `DETAILS`, `REGISTRATION` and
       `Registry` — three spellings of one hierarchy on a page whose job is to
       read as one document. Two of those are the metamodel form's `<legend>`,
       which is a shared contract in BOTH hosts and stays; the third was this
       panel's own `<h3 class=panelTitle>`, a private title system of exactly
       the kind #2283 is converting Console-wide.

       So this adopts `PageSection` rather than inventing a fourth: the outline,
       the spacing contract and the heading level come from the one primitive the
       Console is already migrating to, and `panelTitle` leaves the stylesheet so
       the mechanism cannot come back. `sectionHierarchy.test.ts` pins both. */
    /* ENGRAVED (#2406, leading design): the registry is a section of the
       Details card — "REGISTRY" in the same engraved label as "DETAILS" and
       "REGISTRATION" above it — not a new page heading under the form. */
    <PageSection title="Registry" headingLevel={3} testId="org-registry" appearance="engraved">
      <div className={s.wrap}>

      {/* THE KIT'S SERVER-CHECK ROW (#2406 cards-inputs), which is what this
          panel's private `.rowline` / `.key` / `.value` / `.act` had been
          drawing: the reading's name, its status and what the registry said,
          and the act at the row's end. The kit adds the one thing the copy
          lacked — the status is a live region, so the answer that arrives
          after "Verify now" is SPOKEN rather than silently swapped in. */}
      <ServerCheck
        data-testid="org-vat"
        label="VAT check"
        status={checkStatus(view?.tone, phase)}
        statusLabel={phase === 'checking' ? 'Checking…' : view?.label}
        tone={phase === 'checking' ? undefined : view?.tone}
        detail={phase !== 'checking' && (view?.name || view?.consultation) ? (
          <>
            {view.name}
            {view.name && view.consultation ? ' · ' : null}
            {view.consultation && (
              <span className="tnum" title="VIES consultation number — reverse-charge evidence">№ {view.consultation}</span>
            )}
          </>
        ) : undefined}
        action={mayWrite ? (
          <Button variant="secondary" size="sm" data-testid="vat-verify"
            disabled={phase === 'checking'} onClick={() => void verify()}>
            {phase === 'checking' ? 'Checking…' : result ? 'Re-verify' : 'Verify now'}
          </Button>
        ) : undefined}
      />
      {view?.note && <p className={s.note} data-testid="vat-note">{view.note}</p>}
      {/* The sentence that decides whether this record may be reverse-charged.
          `@solum/vat` refuses reverse charge on a claimed-but-unvalidated id and
          `billing.vat_treatment` fails closed on `p_vat_id_validated` — and a
          format check is a syntax rule, not a register. Saying which of the two
          this is, on the record, is the difference between evidence and a
          green-looking pill. */}
      {result && !isRegistryEvidence(result) && (
        <p className={s.note} data-testid="vat-not-evidence">
          Not registry evidence — a VIES consultation number is what supports reverse charge.
        </p>
      )}
      {view?.persistence && phase !== 'checking' && (
        <p className={s.note} data-testid="vat-persistence"
          role={phase === 'refused' ? 'alert' : undefined}>{view.persistence}</p>
      )}

      {mayWrite && (
        <ServerCheck
          data-testid="org-lookup"
          label="Company register"
          status="idle"
          statusLabel="Not looked up"
          action={(
            <Button variant="secondary" size="sm" data-testid="company-lookup-open"
              onClick={() => setLookupOpen(true)}>Look up</Button>
          )}
        />
      )}
      {lookupRefusal && <p className={s.note} role="alert" data-testid="company-lookup-refusal">{lookupRefusal}</p>}

        <CompanyLookupDialog open={lookupOpen} onClose={() => setLookupOpen(false)} onApply={adopt} />
      </div>
    </PageSection>
  )
}
